import { safeFetch } from './validateFetchUrl'

/**
 * Reload past a stale/truncated hashed chunk. Assets are `Cache-Control: public,
 * immutable` for a year, so `location.reload()` revalidates only the document and
 * replays the bad JS — Refresh then lands back on "New version available".
 *
 * `cache: 'reload'` bypasses the HTTP cache and overwrites the stored entry.
 * Consume the body or the browser may skip the write. Always fall through to
 * `location.reload()`.
 */

const DOCUMENT_TIMEOUT_MS = 3000
const ASSET_TIMEOUT_MS = 8000
const CONCURRENCY = 6
const PURGE_BUDGET_MS = 10000
const ASSET_EXT = /\.(js|css)$/
const CACHE_RELOAD: RequestCache = 'reload'

function timeoutSignal(ms: number): AbortSignal | undefined {
  return typeof AbortSignal.timeout === 'function' ? AbortSignal.timeout(ms) : undefined
}

async function consume(res: Response): Promise<void> {
  if (!res.ok) {
    res.body?.cancel()
    return
  }
  await res.arrayBuffer()
}

async function refetch(url: string, ms: number): Promise<void> {
  const res = await safeFetch(url, { cache: CACHE_RELOAD, signal: timeoutSignal(ms) })
  await consume(res)
}

function sameOriginAssetPath(href: string): string | null {
  try {
    const parsed = new URL(href, window.location.origin)
    if (parsed.origin !== window.location.origin) return null
    if (!ASSET_EXT.test(parsed.pathname)) return null
    return `${parsed.pathname}${parsed.search}`
  } catch {
    return null
  }
}

function addPerfAssetPaths(paths: Set<string>): void {
  for (const { name } of performance.getEntriesByType('resource')) {
    const path = sameOriginAssetPath(name)
    if (path) paths.add(path)
  }
}

function addDocumentAssetPaths(paths: Set<string>): void {
  const nodes = document.querySelectorAll(
    'script[src], link[rel="stylesheet"][href], link[rel="modulepreload"][href], link[rel="preload"][href]',
  )
  nodes.forEach((el) => {
    const href = el.getAttribute('src') || el.getAttribute('href')
    if (!href) return
    const path = sameOriginAssetPath(href)
    if (path) paths.add(path)
  })
}

/** Same-origin JS/CSS from resource timing and the current document. Keep the query
 *  string so the HTTP cache key matches. Do not take only the newest entries: a
 *  failed chunk was measured 164 back in a 209-entry list. */
function loadedAssetPaths(): string[] {
  const paths = new Set<string>()
  try {
    addPerfAssetPaths(paths)
  } catch {
    // resource timing unavailable
  }
  try {
    addDocumentAssetPaths(paths)
  } catch {
    // document scrape failed
  }
  return [...paths]
}

/**
 * Assets fetched by the import that just failed — the actual suspects.
 *
 * Recorded by lazyWithChunkRetry, which brackets the failing import and can tell
 * exactly which files it pulled. Purging those (1-3 files) instead of everything the
 * session ever loaded takes recovery from ~8 MB to a few hundred KB, and sidesteps
 * the 250-entry cap on resource timing that can hide the culprit on busy pages.
 */
let suspectAssetPaths: string[] = []

/** Index into resource timing taken just before a dynamic import is attempted. */
export function markAssetSnapshot(): number {
  try {
    return performance.getEntriesByType('resource').length
  } catch {
    return -1
  }
}

/** Record the same-origin JS/CSS fetched since `mark` as the suspects for recovery. */
export function recordAssetsSince(mark: number): void {
  if (mark < 0) return
  try {
    const paths = new Set<string>()
    for (const { name } of performance.getEntriesByType('resource').slice(mark)) {
      const path = sameOriginAssetPath(name)
      if (path) paths.add(path)
    }
    if (paths.size > 0) suspectAssetPaths = [...paths]
  } catch {
    // resource timing unavailable; recovery falls back to the full sweep
  }
}

async function refetchDocument(): Promise<void> {
  await refetch(`${window.location.pathname}${window.location.search}`, DOCUMENT_TIMEOUT_MS)
}

async function refetchAll(paths: string[]): Promise<void> {
  if (paths.length === 0) return
  let next = 0
  const deadline = Date.now() + PURGE_BUDGET_MS
  const worker = async () => {
    while (next < paths.length && Date.now() < deadline) {
      const path = paths[next++]
      try {
        await refetch(path, ASSET_TIMEOUT_MS)
      } catch {
        // One asset failing must not stop the rest.
      }
    }
  }
  await Promise.all(Array.from({ length: Math.min(CONCURRENCY, paths.length) }, worker))
}

let inFlight: Promise<void> | null = null

async function runReload(sweepWhenUnknown: boolean): Promise<void> {
  if (inFlight) return inFlight
  inFlight = (async () => {
    // Separate try blocks on purpose: a document refetch that times out must not
    // skip the asset purge, which is the part that clears a corrupt cached chunk.
    try {
      await refetchDocument()
    } catch {
      // Offline or blocked — the assets below still matter.
    }
    try {
      if (suspectAssetPaths.length > 0) {
        await refetchAll(suspectAssetPaths)
      } else if (sweepWhenUnknown) {
        await refetchAll(loadedAssetPaths())
      }
    } catch {
      // Best effort; reload regardless.
    }
    window.location.reload()
  })()
  try {
    await inFlight
  } finally {
    inFlight = null
  }
}

/**
 * Automatic post-deploy retry. Refreshes the shell and any recorded suspects, but
 * never sweeps every asset — this runs without the user asking.
 */
export function reloadWithoutCachedShell(): Promise<void> {
  return runReload(false)
}

/**
 * "Refresh page" after a plain reload already failed. Prefers the recorded suspects
 * and falls back to sweeping every loaded asset only when they are unknown.
 */
export function hardReload(): Promise<void> {
  return runReload(true)
}
